// Auth-gated page at /activity showing user engagement history across four tabs:
// Bookmarks, Likes, Discussions, and Flagged articles.
// Radix Tabs (unstyled primitive) provides correct ARIA semantics; FilterPill lacks
// role=tablist/tab/tabpanel. (ref: DL-001)
// Three context providers are mounted at page level because ArticleCard calls their
// hooks unconditionally; missing any provider causes a runtime error. (ref: DL-007)
import { useState } from "react";
import * as Tabs from "@radix-ui/react-tabs"; // ui-migration-guardrails: allow-radix-import
import { Helmet } from "react-helmet-async";
import { useQuery, getActivityCounts } from "wasp/client/operations";
import { BookmarksProvider } from "../articles/BookmarksContext";
import { ArticleEngagementProvider } from "../articles/ArticleEngagementContext";
import { ArticleFlagProvider } from "../articles/ArticleFlagContext";
import { BookmarksTab } from "./components/BookmarksTab";
import { LikesTab } from "./components/LikesTab";
import { DiscussionsTab } from "./components/DiscussionsTab";
import { FlagsTab } from "./components/FlagsTab";

type TabValue = "bookmarks" | "likes" | "discussions" | "flags";

// ArticleCard calls context hooks unconditionally; all three providers are required
// at page level to avoid runtime errors. (DL-007)
export function ActivityPage() {
  return (
    <BookmarksProvider>
      <ArticleEngagementProvider>
        <ArticleFlagProvider>
          <ActivityPageContent />
        </ArticleFlagProvider>
      </ArticleEngagementProvider>
    </BookmarksProvider>
  );
}

function TabCount({ count }: { count: number | undefined }) {
  if (count === undefined) return null;
  return (
    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-neutral-500 dark:text-neutral-400">
      {count}
    </span>
  );
}

function ActivityPageContent() {
  const [activeTab, setActiveTab] = useState<TabValue>("bookmarks");

  // Lightweight COUNT-only query for tab badges; per-tab data queries remain lazy. (Devin review)
  const { data: counts } = useQuery(getActivityCounts);

  const tabs: { value: TabValue; label: string; count: number | undefined }[] =
    [
      { value: "bookmarks", label: "Bookmarks", count: counts?.bookmarks },
      { value: "likes", label: "Likes", count: counts?.likes },
      {
        value: "discussions",
        label: "Discussions",
        count: counts?.discussions,
      },
      { value: "flags", label: "Flagged", count: counts?.flags },
    ];

  return (
    <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <Helmet>
        <title>My Activity - Optimistic News</title>
        <link rel="canonical" href="https://optimistic-news.com/activity" />
      </Helmet>

      <Tabs.Root
        defaultValue="bookmarks"
        onValueChange={(value) => setActiveTab(value as TabValue)}
      >
        <Tabs.List className="flex justify-center gap-1 border-b border-neutral-200 dark:border-neutral-700 mb-6">
          {tabs.map(({ value, label, count }) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="px-4 py-2.5 text-sm font-medium text-neutral-500 dark:text-neutral-400 border-b-2 border-transparent -mb-px transition-colors duration-150 hover:text-neutral-900 dark:hover:text-white data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              {label}
              <TabCount count={count} />
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="bookmarks">
          <BookmarksTab isActive={activeTab === "bookmarks"} />
        </Tabs.Content>

        <Tabs.Content value="likes">
          <LikesTab isActive={activeTab === "likes"} />
        </Tabs.Content>

        <Tabs.Content value="discussions">
          <DiscussionsTab isActive={activeTab === "discussions"} />
        </Tabs.Content>

        <Tabs.Content value="flags">
          <FlagsTab isActive={activeTab === "flags"} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
